<template>
  <div>
    <b-form @submit.stop.prevent="onSubmit">
      <b-form-radio-group
        v-model="form.role"
        v-if="visibleRole && ifAuthenticated"
        @change="setValue('role', $event)"
        :options="options"
        :state="validateState('role')"
        name="radio-role"
        class="mb-4"
      >
        <b-form-invalid-feedback :state="validateState('role')"
          >Please select one</b-form-invalid-feedback
        >
      </b-form-radio-group>
      <b-form-group label-cols="4" label="Prénom" label-for="input-name">
        <b-form-input
          id="input-name"
          name="input-name"
          @change="setValue('name', $event)"
          :value="form.name"
          :state="validateState('name')"
          aria-describedby="input-name"
          type="text"
        ></b-form-input>

        <b-form-invalid-feedback id="input-name">
          Ce champ est requis
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label-cols="4" label="Nom" label-for="input-lastname">
        <b-form-input
          id="input-lastname"
          name="input-lastname"
          @change="setValue('last_name', $event)"
          :value="form.last_name"
          :state="validateState('last_name')"
          aria-describedby="input-lastname"
          type="text"
        ></b-form-input>

        <b-form-invalid-feedback id="input-lastname"
          >Ce champ est requis
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label-cols="4" label="Courriel" label-for="input-email">
        <b-form-input
          id="input-email"
          name="input-email"
          @change="setValue('email', $event)"
          :value="form.email"
          :state="validateState('email')"
          aria-describedby="input-email"
          type="email"
        ></b-form-input>

        <b-form-invalid-feedback id="input-email">
          Ce champ est requis et doit être un courriel valide
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group
        label="Établissement(s) d'enseignement"
        label-for="input-objective"
      >
        <b-form-textarea
          id="location-input"
          name="location-input"
          @change="setValue('location', $event)"
          :value="form.location"
          :state="validateState('location')"
          aria-describedby="input-location-live-feedback"
          type="text"
        ></b-form-textarea>

        <b-form-invalid-feedback id="input-location-live-feedback"
          >Ce champ est requis
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Cours" label-for="input-subject">
        <b-form-textarea
          id="subject-input"
          name="subject-input"
          @change="setValue('subject', $event)"
          :value="form.subject"
          :state="validateState('subject')"
          aria-describedby="input-subject-live-feedback"
          type="text"
        ></b-form-textarea>

        <b-form-invalid-feedback id="input-subject-live-feedback"
          >Ce champ est requis
        </b-form-invalid-feedback>
      </b-form-group>
    </b-form>

    <div class="d-flex justify-content-end mb-5">
      <template v-if="!$route.params.id">
        <b-btn pill variant="outline-secondary" @click="resetForm">
          Réinitialiser
        </b-btn>

        <b-btn
          pill
          @click="onCreateUser"
          variant="primary"
          :disabled="createStatus == 'fetching'"
          class="ml-2"
          v-if="visibleRole && ifAuthenticated"
        >
          <span>Créer l'utilisateur et envoyer une invitation</span>
          <b-spinner
            class="ml-2"
            small
            v-if="createStatus == 'fetching'"
          ></b-spinner>
        </b-btn>

        <b-btn
          pill
          @click="onRegister"
          variant="primary"
          :disabled="registerStatus == 'fetching'"
          class="ml-2"
          v-else
        >
          <span>Envoyer votre demande</span>
          <b-spinner
            class="ml-2"
            small
            v-if="registerStatus == 'fetching'"
          ></b-spinner>
        </b-btn>
      </template>
      <template v-else>
        <b-btn
          pill
          @click="onUpdateUser"
          variant="primary"
          :disabled="createStatus == 'fetching'"
          class="ml-2"
          v-if="ifAuthenticated"
        >
          <span>Sauvegarder</span>
          <b-spinner
            class="ml-2"
            small
            v-if="createStatus == 'fetching'"
          ></b-spinner>
        </b-btn>
      </template>
    </div>
  </div>
</template>

<script>
/**
 * @file       User creation form component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import { required, email, maxLength } from "vuelidate/lib/validators";
import { mapActions, mapState } from "vuex";

const initFromData = {
  role: null,
  name: null,
  last_name: null,
  email: null,
  location: null,
  subject: null
};

/**
 * Export the component.
 * 
 * @type  {Object}
 */
export default {
  props: {
    visibleRole: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      form: Object.assign({}, initFromData),
      options: [
        { text: "Administrateur", value: "admin" },
        { text: "Enseignant", value: "teacher" }
      ]
    };
  },

  /**
   * Vuelidate form validations.
   * 
   * @type  {Object}
   */
  validations: {
    form: {
      role: { required },
      name: { required },
      last_name: { required },
      email: { required, email },
      location: { required, maxLength: maxLength(140) },
      subject: { required, maxLength: maxLength(140) }
    }
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    ...mapState({
      registerStatus: state => state.teachers.register.status,
      createStatus: state => state.admin.user.status,
      ifAuthenticated: state => !!state.auth.profile.data,
      profile: state => state.admin.user.data
    })
  },
  mounted() {
    this.isTeacherRequest();
    if (
      this.$route.name == "admin.usersProfile" &&
      parseInt(this.$route.params.id) != null
    ) {
      this.form.role = this.profile.role;
      this.form.name = this.profile.name;
      this.form.last_name = this.profile.last_name;
      this.form.email = this.profile.email;
      this.form.location = this.profile.teacher.location;
      this.form.subject = this.profile.teacher.subject;
      this.form.reason = this.profile.teacher.reason;
    }
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    ...mapActions({
      register: "teachers/register",
      createUser: "admin/createUser",
      updateUser: "admin/putUser"
    }),

    /**
     * Check if teacher request.
     */
    isTeacherRequest() {
      if (!this.ifAuthenticated && this.$route.name == "teachers.register")
        this.form.role = "teacher";
    },

    /**
     * Sets the form value.
     *
     * @param      {string}  key     The new value
     * @param      {string}  value   The value
     */
    setValue(key, value) {
      this.$v.form[key].$model = value;
      this.$v.form[key].$touch();
    },

    /**
     * Validate the form state.
     *
     * @param      {string}  name    The input name
     *
     * @return     {bool}  Dirty or not.
     */
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },

    /**
     * Resets the form.
     */
    resetForm() {
      this.form = Object.assign({}, initFromData);
      this.form.role = this.isTeacherRequest();
      // Reset on next tick
      this.$nextTick(() => {
        this.$v.$reset();
      });
    },

    /**
     * Register.
     */
    onRegister() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }
      this.register(this.form)
        .then(() => {
          this.$root.$bvToast.toast(`Demande envoyée`, { title: "Succès" });
          this.resetForm();
        })
        .catch(err => {
          this.$root.$bvToast.toast(
            err.response
              ? err.response.data.message == "Email duplicated"
                ? "Cette adresse courriel est déjà utilisé"
                : "La demande à échoué"
              : "La demande à échoué",
            {
              title: "Erreur",
              variant: "danger"
            }
          );
        });
    },

    /**
     * Create user.
     */
    onCreateUser() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }

      this.createUser(this.form)
        .then(() => {
          this.$root.$bvToast.toast(`Enregistrement avec succès.`, {
            title: "Succès"
          });
          this.resetForm();
        })
        .catch(err => {
          this.$root.$bvToast.toast(
            err.response
              ? err.response.data.message == "Email duplicated"
                ? "Cette adresse courriel est déjà utilisé"
                : "La demande à échoué"
              : "La demande à échoué",
            {
              title: "Erreur",
              variant: "danger"
            }
          );
        });
    },

    /**
     * Update user.
     */
    onUpdateUser() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }
      this.form.user_id = this.$route.params.id;
      this.updateUser(this.form)
        .then(() => {
          this.$root.$bvToast.toast(`Enregistrement avec succès.`, {
            title: "Succès"
          });
        })
        .catch(err => {
          this.$root.$bvToast.toast(
            err.response
              ? err.response.data.message == "Email duplicated"
                ? "Cette adresse courriel est déjà utilisé"
                : "La demande à échoué"
              : "La demande à échoué",
            {
              title: "Erreur",
              variant: "danger"
            }
          );
        });
    }
  }
};
</script>

<style lang="scss" scoped></style>
